/**
 * V2QsRelativeOnset 컴포넌트 Props 인터페이스
 * V1 QsInputRelativeOnset의 V2 버전 (순수 UI만)
 *
 * 상대적 시간 입력 컴포넌트 (예: "2시간 전", "3일 전")
 */

/**
 * 시간 단위 타입
 */
export type V2QsRelativeOnsetUnit = 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

/**
 * 상대적 시간 값 인터페이스
 */
export interface V2QsRelativeOnsetValue {
  /**
   * 숫자 값 (몇 시간/일 전인지)
   */
  duration: number;

  /**
   * 시간 단위
   */
  unit: V2QsRelativeOnsetUnit;

  /**
   * 계산된 날짜/시간 문자열 (ISO 또는 포맷된 문자열)
   */
  calculatedDate?: string;
}

/**
 * 단위 옵션 아이템
 */
export interface V2QsRelativeOnsetUnitOption {
  /**
   * 단위 키
   */
  value: V2QsRelativeOnsetUnit;

  /**
   * 표시할 라벨 (단수형)
   */
  labelSingular: string;

  /**
   * 표시할 라벨 (복수형)
   */
  labelPlural: string;
}

export interface V2QsRelativeOnsetProps {
  /**
   * 현재 값
   */
  value?: V2QsRelativeOnsetValue;

  /**
   * 값 변경 시 콜백
   */
  onChange?: (value: V2QsRelativeOnsetValue) => void;

  /**
   * 기준 날짜 (기본: 현재 시간)
   */
  baseDate?: Date;

  /**
   * 기본 단위 (기본: 'hour')
   */
  defaultUnit?: V2QsRelativeOnsetUnit;

  /**
   * 단위 옵션 (외부에서 i18n 텍스트 전달)
   */
  unitOptions?: V2QsRelativeOnsetUnitOption[];

  /**
   * 비활성화 여부
   */
  disabled?: boolean;

  /**
   * 읽기 전용 여부
   */
  readOnly?: boolean;

  /**
   * 에러 상태
   */
  hasError?: boolean;

  /**
   * 에러 메시지
   */
  errorMessage?: string;

  /**
   * 계산된 날짜 표시 여부 (기본: true)
   */
  showCalculatedDate?: boolean;

  /**
   * 계산된 날짜 포맷 함수
   */
  formatCalculatedDate?: (date: Date, unit: V2QsRelativeOnsetUnit) => string;

  /**
   * 추가 CSS 클래스
   */
  className?: string;

  /**
   * 입력 필드 추가 CSS 클래스
   */
  inputClassName?: string;

  /**
   * Picker 추가 CSS 클래스
   */
  pickerClassName?: string;
}

/**
 * 기본 단위 옵션
 */
export const defaultUnitOptions: V2QsRelativeOnsetUnitOption[] = [
  { value: 'minute', labelSingular: 'minute ago', labelPlural: 'minutes ago' },
  { value: 'hour', labelSingular: 'hour ago', labelPlural: 'hours ago' },
  { value: 'day', labelSingular: 'day ago', labelPlural: 'days ago' },
  { value: 'week', labelSingular: 'week ago', labelPlural: 'weeks ago' },
  { value: 'month', labelSingular: 'month ago', labelPlural: 'months ago' },
  { value: 'year', labelSingular: 'year ago', labelPlural: 'years ago' },
];

export const defaultV2QsRelativeOnsetProps: Partial<V2QsRelativeOnsetProps> = {
  defaultUnit: 'hour',
  disabled: false,
  readOnly: false,
  hasError: false,
  showCalculatedDate: true,
};
